import styled, { keyframes } from "styled-components";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const ACCENT = "#FF8F40";
const TEXT_PRIMARY = "#1d1d1f";
const TEXT_SECONDARY = "#86868b";
const BG_SOFT = "#f5f5f7";

export const LoginPage = styled.div`
  display: flex;
  height: 100vh;
  width: 100vw;
  background-color: #ffffff;
  font-family: "SF Pro Display", -apple-system, BlinkMacSystemFont,
    "Segoe UI", Roboto, sans-serif;
`;

export const InfoSide = styled.div`
  flex: 1.4;
  position: relative;
  background-image: url("/login.png");
  background-size: cover;
  background-position: 50%  50%;
  margin: 20px;
  border-radius: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  @media (max-width: 1024px) {
    display: none;
  }

  .info-wrapper {
    position: relative;
    z-index: 2;
    padding: 80px;
    animation: ${fadeIn} 1s ease-out;
  }
`;

export const NoiseOverlay = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.12;
  background-image: url("https://grainy-gradients.vercel.app/noise.svg");
  filter: contrast(120%) brightness(105%);
`;

export const GlassCard = styled.div`
  background: linear-gradient(
    180deg,
    rgba(255, 143, 64, 0.80) 0%,
    rgba(180, 70, 10, 0.80) 100%
  );
  backdrop-filter: blur(22px);
  -webkit-backdrop-filter: blur(22px);
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 20px;
  padding: 48px;
  color: #3D3A25;
  box-shadow: 0 30px 60px rgba(0, 0, 0, 0.25);

  .hl {
    margin: 0 2px;
    font-weight: 700;
    color: #ffffff;
  }

  h3 {
    margin: 0 0 20px 0;
    font-size: 2.2rem;
    font-weight: 600;
    letter-spacing: -0.02em;
    color: #ffffff99;
  }

  p {
    margin: 0;
    font-size: 1.2rem;
    color: rgba(255,255,255,0.9);
    line-height: 1.5;
    font-weight: 400;
  }

  .stat {
    margin-top: 24px;

    .value {
      font-size: 4rem;
      font-weight: 700;
      background: linear-gradient(
        180deg,
        #ffffff 0%,
        rgba(255,255,255,0.6) 100%
      );
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      margin-right: 18px;
    }

    .label {
      font-size: 0.9rem;
      color: rgba(255,255,255,0.85);
      font-weight: 600;
      letter-spacing: 0.04em;
    }
  }
`;

export const FormSide = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px;
  animation: ${fadeIn} 0.8s ease-out;

  .content {
    width: 100%;
    max-width: 400px;
  }

  header {
    text-align: center;
    margin-bottom: 48px;

    img {
      height: 40px;
      margin-bottom: 24px;
    }
  }
`;

export const Input = styled.input`
  width: 100%;
  height: 52px;
  padding: 0 12px;
  font-size: 14px;
  border: 1px solid #d2d2d7;
  border-radius: 8px;
  outline: none;
  background: #ffffff;
  transition: all 0.2s ease-in-out;
  color: ${TEXT_PRIMARY};

  &:focus {
    border-color: ${ACCENT};
    box-shadow: 0 0 0 3px rgba(255, 143, 64, 0.15);
  }
`;

export const SubmitButton = styled.button`
  margin-top: 24px;
  height: 52px;
  background: ${ACCENT};
  color: white;
  min-width: 100%;
  border: none;
  border-radius: 12px;
  font-size: 17px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translateY(-.5px);
    box-shadow: 0 10px 20px rgba(0,0,0,0.05);
  }
`;

export const Footer = styled.footer`
  position: absolute;
  bottom: 40px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  opacity: 0.6;

  span {
    font-size: 16px;
    font-weight: 600;
    color: ${TEXT_SECONDARY};
  }

  img {
    height: 45px;
    filter: grayscale(1);
  }
`;

export const AiBadge = styled.div`
  display: inline-block;
  padding: 6px 14px;
  background: rgba(255, 255, 255, 0.25);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 100px;
  color: #3D3A25;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 24px;
`;

export const Subtitle = styled.p`
  font-size: 17px;
  color: ${TEXT_SECONDARY};
  line-height: 1.47;
  letter-spacing: -0.022em;
  max-width: 280px;
  margin: 0 auto;
`;

export const Form = styled.form`
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: center;

  * {
    box-sizing: border-box;
  }
`;

export const InputGroup = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-top: 12px;
`;

export const Label = styled.label`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${TEXT_SECONDARY};
  pointer-events: none;
  transition: all 0.2s ease-out;
  font-size: 14px;

  ${Input}:focus + &,
  ${Input}:not(:placeholder-shown) + & {
    top: -10px;
    left: 8px;
    font-size: 11px;
    font-weight: 600;
    color: ${ACCENT};
    background-color: #ffffff;
    padding: 0 4px;
    transform: translateY(0);
  }
`;

export const ErrorMessage = styled.div`
  background: #fff2f2;
  color: #ff3b30;
  padding: 12px;
  border-radius: 10px;
  font-size: 14px;
  text-align: center;
  margin-bottom: 20px;
  display: none;
  border: 1px solid rgba(255, 59, 48, 0.2);
  width: 100%;

  &.show {
    display: block;
    animation: ${fadeIn} 0.4s ease;
  }
`;
